import { createWelcomeStep, createMiddleStep, createFinalStep } from '../config/stepTemplates';
import { waitForElement } from '../utils/tourUtils';

const TOTAL = 9;

export function getLegalNewsTourSteps(t, { navigate }) {
  return [
    // 1 — Welcome (centered)
    createWelcomeStep({
      id: 'news-welcome',
      title: t('onboarding_tour.legalNews.step1.title', 'Search News'),
      text: t('onboarding_tour.legalNews.step1.text', 'Search through curated legal news and regulatory updates.'),
      t,
      totalSteps: TOTAL,
    }),

    // 2 — Categories
    {
      ...createMiddleStep({
        id: 'news-categories',
        title: t('onboarding_tour.legalNews.step2.title', 'Filter by Category'),
        text: t('onboarding_tour.legalNews.step2.text', 'Browse by topic: corporate, tax, labour, IP, and more.'),
        attachTo: { element: '[data-tour="news-categories"]', on: 'right' },
        t,
        stepIndex: 2,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="news-categories"]', 5000).catch(() => {});
      },
    },

    // 3 — News card
    {
      ...createMiddleStep({
        id: 'news-card',
        title: t('onboarding_tour.legalNews.step3.title', 'News Articles'),
        text: t('onboarding_tour.legalNews.step3.text', 'Summary, source, date, category. Click for the full article.'),
        attachTo: { element: '[data-tour="news-card"]', on: 'bottom' },
        t,
        stepIndex: 3,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="news-card"]', 5000).catch(() => {});
      },
    },

    // 4 — Bookmark
    {
      ...createMiddleStep({
        id: 'news-bookmark',
        title: t('onboarding_tour.legalNews.step4.title', 'Save for Later'),
        text: t('onboarding_tour.legalNews.step4.text', 'Bookmark articles to read later from your bookmarks tab.'),
        attachTo: { element: '[data-tour="news-bookmark"]', on: 'left' },
        t,
        stepIndex: 4,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="news-bookmark"]', 5000).catch(() => {});
      },
    },

    // 5 — Preferences link → navigate to preferences page
    {
      id: 'news-go-preferences',
      title: t('onboarding_tour.legalNews.step5.title', 'Customize Your Feed'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((5 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 5, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.legalNews.step5.text', 'Set preferences for your practice area and jurisdiction.')}</p>`,
      attachTo: { element: '[data-tour="news-preferences"]', on: 'bottom' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        {
          text: t('onboarding_tour.next', 'Next'),
          classes: 'shepherd-button-primary',
          action: function () {
            navigate('/legal-news/preferences');
            const tour = this;
            waitForElement('[data-tour="news-pref-categories"]', 5000)
              .then(() => setTimeout(() => tour.next(), 300))
              .catch(() => tour.next());
          },
        },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="news-preferences"]', 5000).catch(() => {});
      },
    },

    // 6 — Preferences: Categories
    {
      ...createMiddleStep({
        id: 'news-pref-categories',
        title: t('onboarding_tour.legalNews.step_pref_categories.title', 'Choose Topics'),
        text: t('onboarding_tour.legalNews.step_pref_categories.text', 'Select the legal categories that matter to you. Your feed will prioritize articles from these topics.'),
        attachTo: { element: '[data-tour="news-pref-categories"]', on: 'bottom' },
        t,
        stepIndex: 6,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="news-pref-categories"]', 5000).catch(() => {});
      },
    },

    // 7 — Preferences: Keywords
    {
      ...createMiddleStep({
        id: 'news-pref-keywords',
        title: t('onboarding_tour.legalNews.step_pref_keywords.title', 'Add Keywords'),
        text: t('onboarding_tour.legalNews.step_pref_keywords.text', 'Add specific keywords to track. You\'ll be notified when new articles match your keywords.'),
        attachTo: { element: '[data-tour="news-pref-keywords"]', on: 'bottom' },
        t,
        stepIndex: 7,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="news-pref-keywords"]', 3000).catch(() => {});
      },
    },

    // 8 — Preferences: Email Digest
    {
      ...createMiddleStep({
        id: 'news-pref-digest',
        title: t('onboarding_tour.legalNews.step_pref_digest.title', 'Email Digest'),
        text: t('onboarding_tour.legalNews.step_pref_digest.text', 'Enable daily, weekly, or monthly email digests to receive curated news directly in your inbox.'),
        attachTo: { element: '[data-tour="news-pref-digest"]', on: 'top' },
        t,
        stepIndex: 8,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="news-pref-digest"]', 3000)
          .then((el) => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return new Promise((r) => setTimeout(r, 300));
          })
          .catch(() => {});
      },
    },

    // 9 — Save preferences
    {
      ...createFinalStep({
        id: 'news-pref-save',
        title: t('onboarding_tour.legalNews.step_pref_save.title', 'Save Preferences'),
        text: t('onboarding_tour.legalNews.step_pref_save.text', 'Once you\'ve configured your preferences, click Save to apply them. Your feed will update accordingly.'),
        attachTo: { element: '[data-tour="news-pref-save"]', on: 'top' },
        t,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="news-pref-save"]', 3000)
          .then((el) => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return new Promise((r) => setTimeout(r, 300));
          })
          .catch(() => {});
      },
    },
  ];
}
